import type {
  Flow_AbstractComponent,
  Flow_Diff,
  Flow_ComponentProps,
  Flow_ObjMap,
} from 'src/types/flow-to-typescript-codemod';
import type {Option} from './types';

import * as React from 'react';
import {useDispatch} from 'react-redux';
import first from 'lodash/first';
import last from 'lodash/last';
import kebabCase from 'lodash/kebabCase';
import uniq from 'lodash/uniq';

import classify from 'src/utils/classify';
import logger from 'src/utils/logger';

import {
  useFilteredOptions,
  useTypeahead,
  useSelect,
  useSenseTypeahead,
  useTokenInput,
  getDefaultState,
  reduceDefault,
  type SenseTypeaheadProps,
} from './hooks';
import {showGenericError} from 'src/action-creators/modal';

import {DEFAULT_LIMIT_VALUE} from './constants';

import ArrowDownIcon from 'src/images/arrow-down.svg';
import CancelIcon from 'src/images/close-x-thick.svg';
import SearchIcon from 'src/images/search-icon.svg';

import css from './style.css';
import legacyCss from './legacy.css';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

/**
 * TODO
 * - fix all global .Select styles
 * - variable height for token-list-input?
 * - variable picker dropdown
 * - limit height?
 * - error box?
 * - make a new messaging contact input
 * - actions?
 * - make it work with the variable selector
 * - replicate old token-list-input
 */

type AutocompleteProps = {
  value: string;
  options: string[];
  onChange: (arg1: string) => unknown;
  onSelect?: (arg1: string) => unknown;
  name?: string;
  emptyOption?: string;
  clearable?: boolean;
  prefixLabel?: React.ReactNode;
  placeholder?: string;
  error?: unknown;
  baseClassNames?: InputAndOptionsClassNames;
  classNames?: InputAndOptionsClassNames;
  clearOnSelect?: boolean;
};

/**
 * a text input that provides suggestions
 *
 * Useful for situations where the user is not restricted to a short list
 * of options but may want hints or reminders of useful values (e.g. search fields).
 *
 * @param  props  the react props
 * @param  props.value  the controlled value of the input
 * @param  props.options  all possible suggestions that will be filtered by the user
 * @param  props.onChange  a handler that is called whenever the value changes
 * @param  props.onSelect  a handler that is called whenever a value from suggestions is selected
 * @param  props.name  the name of the text input (useful if its in a form)
 */
export function Autocomplete({
  value,
  options,
  onChange,
  onSelect,
  name,
  clearable,
  prefixLabel,
  placeholder,
  error,
  baseClassNames,
  classNames,
  emptyOption,
  clearOnSelect = false,
}: AutocompleteProps) {
  const handleSelect = (option: string) => {
    if (clearOnSelect) {
      // clearing the input on select
      onChange('');
    } else {
      onChange(option);
    }
    onSelect && onSelect(option);
    inputProps.ref.current?.blur();
  };

  const [state, dispatch] = React.useReducer(
    reduceDefault,
    null,
    getDefaultState,
  );
  const {focused, selectedIndex} = state;
  const excludedValues = React.useMemo(() => [value], [value]);

  const [filteredOptions] = useFilteredOptions({
    searchTerm: value,
    options,
    excludedValues,
    searchOptionsBy: (option, searchTerm) => option.includes(searchTerm),
  });
  const [inputProps, handleContainerMouseDown] = useTypeahead({
    state,
    dispatch,
    options: filteredOptions,
    onAddOption: handleSelect,
    isOptionDisabled: (_) => false,
  });

  const isShowingEmptyOption =
    focused && filteredOptions.length === 0 && emptyOption;
  const isShowingOptions = focused && filteredOptions.length > 0;

  const showClearButton = clearable && value && !focused;

  return (
    <InputAndOptions
      baseClassNames={baseClassNames}
      classNames={classNames}
      error={error}
      options={
        filteredOptions.length > 0 ? filteredOptions : [emptyOption ?? '']
      }
      selectedIndex={selectedIndex}
      onSelect={isShowingEmptyOption ? () => false : handleSelect}
      onContainerMouseDown={handleContainerMouseDown}
      // @ts-expect-error - TS2322 - Type '{ onBlur: (_event: SyntheticEvent<HTMLInputElement, Event>) => void; onFocus: (_event: SyntheticEvent<HTMLInputElement, Event>) => void; ... 6 more ...; placeholder: string | undefined; }' is not assignable to type '{ disabled?: unknown; } & ClassAttributes<HTMLInputElement> & InputHTMLAttributes<HTMLInputElement>'.
      inputProps={{
        name,
        value,
        onChange: (event) => onChange(event.currentTarget.value),
        placeholder,
        ...inputProps,
      }}
      isFocused={focused}
      isShowingOptions={isShowingOptions || isShowingEmptyOption}
      prefixLabel={
        prefixLabel && <div className={css.prefixLabel}>{prefixLabel}</div>
      }
      suffixLabel={
        showClearButton && (
          <div className={css.clearButton} onMouseDown={() => onChange('')}>
            <CancelIcon />
          </div>
        )
      }
      resolveLabel={(value) => value}
      resolveKey={(value) => value}
      isArbitrary={(_value) => false}
    />
  );
}

export function SearchInput(props: AutocompleteProps) {
  return <Autocomplete prefixLabel={<SearchIcon />} {...props} />;
}

export type TokenListInputClassNames = Readonly<
  InputAndOptionsClassNames & {
    outerContainer?: string | null | undefined;
  }
>;

// TODO (kyle): we need to split this out into two components,
// one that requires the resolvers and one that expects an
// Option type.
export type TokenListInputProps<V> = {
  values: V[];
  onChange: (arg1: V[]) => unknown;
  inputProps?: React.ComponentProps<'input'>;
  name?: string;
  limit?: number;
  showValuesOutside?: unknown;
  error?: unknown;
  isExpandable?: unknown;
  isHeightLimited?: unknown;
  baseClassNames?: InputAndOptionsClassNames;
  classNames?: TokenListInputClassNames;
  enableListPasting?: boolean;
  components?: SuggestionsComponents<V> & {
    Token?: React.ComponentType<TokenProps<V>>;
  };
  prefixLabel?: React.ReactNode;
  placeholder?: string | null | undefined;
  hasLabel?: boolean;
  autofocus?: boolean;
} & SenseTypeaheadProps<V> & {
    resolveLabel?: (
      arg1: V,
    ) => string | React.ReactElement<React.ComponentProps<'div'>>;
    resolveKey?: (arg1: V) => string;
    makeValue?: (arg1: string) => V;
    disabled?: unknown;
  };

/**
 * an input that allows for a variable number of unique values
 *
 * Useful for allowing the user to build lists of items.
 *
 * @param  props.values  the controlled list of values
 * @param  props.onChange  the handler that is fired when the user tries to change the values
 * @param  props.name  the name of the input (useful if it is part of a form)
 * @param  props.limit  the maximum number of item the user can add
 * @param  props.TokenComponent  a custom component used to render each token within the input
 * @param  props.showValuesOutside  if true, clickable tokens will show up above and outside the input.
 *                                  useful when the list can get very large.
 *
 * @param  props.options  total list of allowed values for this field. will be used to provide suggestions.
 * @param  props.searchable  whether or not to filter the list of options when the user types in the input
 * @param  props.allowArbitraryValues  whether or not the user can specify their own custom values
 * @param  props.validateArbitraryValue  a function that determines if a custom value is valid
 * @param  props.formatArbitraryOptionLabel  a function that customizes the custom value when it shows up as a suggestion
 * @param  props.searchOnEmptyString  whether or not to show suggestions when there is no text in the search input
 * @param  props.groupOptionsBy  a function that produces a category key for any given option. if specified, suggestions will be grouped into sections by category.
 * @param  props.sortOptionsBy  a comparison function that determines the sort order of suggestions. by default it sorts them alphabetically.
 */
export function TokenListInput<V>(
  props: TokenListInputProps<V>,
): React.ReactElement<React.ComponentProps<'div'>> {
  const {
    values,
    onChange,
    name,
    limit = Infinity,
    searchable,
    showValuesOutside,
    error,
    isExpandable,
    isHeightLimited,
    enableListPasting,
    baseClassNames = legacyCss,
    classNames,
    prefixLabel,
    placeholder,
    hasLabel,
    components: {Token = DefaultToken, ...components} = {},
    autofocus,
    // @ts-expect-error - TS2339 - Property 'value' does not exist on type 'V'.
    resolveKey = (option: V) => option.value,
    // @ts-expect-error - TS2339 - Property 'label' does not exist on type 'V'.
    resolveLabel = (option: V) => option.label,
    // @ts-expect-error - TS2322 - Type '{ value: string; label: string; }' is not assignable to type 'V'.
    makeValue = (string: string): V => ({value: string, label: string}),
    // @ts-expect-error - TS2339 - Property 'arbitrary' does not exist on type 'V'.
    isArbitrary = (option: V) => option.arbitrary,
    disabled,
  } = props;

  const dispatch = useDispatch();

  const handleAddOption = (option: V) => {
    onChange([...values, option]);
  };

  const excludedValues = values;
  const isLimited = values.length >= limit;

  const [
    {selectedIndex, searchTerm},
    filteredOptions,
    groupedOptions,
    inputProps,
    handleContainerMouseDown,
    handleSelect,
    {isSearching, focused, isShowingOptions},
  ] = useSenseTypeahead({
    onAddOption: handleAddOption,
    excludedValues,
    isLimited,
    ...props,
    resolveKey,
    resolveLabel,
  });

  const tokens: Array<React.ReactNode> = useTokens<V>({
    values,
    onChange,
    resolveKey,
    resolveLabel,
    inputRef: inputProps.ref,
    enableListPasting,
    Token,
    disabled: Boolean(disabled),
  });

  return (
    <div className={classify(css.container, classNames?.outerContainer)}>
      {showValuesOutside ? (
        <div className={css.tokensOutside}>{tokens}</div>
      ) : null}
      <InputAndOptions
        resolveKey={resolveKey}
        resolveLabel={resolveLabel}
        isArbitrary={isArbitrary}
        baseClassNames={baseClassNames}
        classNames={classNames}
        components={components}
        error={error}
        isLimited={isLimited}
        isExpandable={isExpandable}
        isHeightLimited={isHeightLimited}
        options={props.groupOptionsBy ? groupedOptions : filteredOptions}
        selectedIndex={searchable ? selectedIndex : null}
        onSelect={handleSelect}
        onContainerMouseDown={handleContainerMouseDown}
        autofocus={autofocus}
        inputProps={{
          ...props.inputProps,
          // @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'boolean | undefined'.
          disabled,
          outerRef: props.inputProps?.ref,
          // @ts-expect-error - TS2783 - 'onKeyDown' is specified more than once, so this usage will be overwritten.
          onKeyDown: (event) => {
            if (event.key === 'Backspace' && searchTerm === '') {
              event.preventDefault();
              onChange(values.slice(0, -1));
            } else {
              inputProps.onKeyDown(event);
            }
          },
          // @ts-expect-error - TS2322 - Type '(event: ClipboardEvent) => void' is not assignable to type 'ClipboardEventHandler<HTMLInputElement>'.
          onPaste: (event: ClipboardEvent) => {
            if (!enableListPasting || !makeValue) {
              return;
            }
            event.preventDefault();
            const value = event.clipboardData?.getData('text');
            if (!value || !value.length) {
              return;
            }
            const parsedValues = value
              .split(/[\,\n]/)
              .reduce<Array<any>>((acc, val) => {
                val = val.trim();
                if (
                  // value exists
                  val.length > 0 &&
                  // value not already in input
                  !values.map((value) => resolveKey(value)).includes(val) &&
                  // value not already in queue
                  !acc.includes(val)
                ) {
                  acc.push(val);
                }
                return acc;
              }, [])
              .map(makeValue);

            if (values.length + parsedValues.length <= DEFAULT_LIMIT_VALUE) {
              onChange([...values, ...parsedValues]);
            } else {
              dispatch(
                showGenericError({
                  title: 'Too many entries',
                  text: `Limit is ${DEFAULT_LIMIT_VALUE}`,
                }),
              );
            }
          },
          ...inputProps,
        }}
        isSearching={isSearching}
        isFocused={focused}
        isShowingOptions={isShowingOptions}
        prefixLabel={prefixLabel}
      >
        {!showValuesOutside && tokens}
        {placeholder && (
          <Placeholder
            className={baseClassNames.placeholder}
            placeholder={placeholder}
            isLabel={hasLabel}
            isHidden={isSearching || tokens.length > 0}
          />
        )}
      </InputAndOptions>
      {values.map((option) => (
        <input
          type="hidden"
          name={name}
          key={resolveKey(option)}
          value={resolveKey(option)}
        />
      ))}
    </div>
  );
}

export type TokenProps<V> = {
  className: string;
  option?: V;
  onClick:
    | ((arg1: React.SyntheticEvent<Element>) => unknown)
    | null
    | undefined;
  children: string | React.ReactElement<React.ComponentProps<'div'>>;
};
function DefaultToken<V>({className, onClick, children}: TokenProps<V>) {
  return (
    // @ts-expect-error - TS2322 - Type '((arg1: SyntheticEvent<Element, Event>) => unknown) | null | undefined' is not assignable to type 'MouseEventHandler<HTMLDivElement> | undefined'.
    <div className={classify(css.defaultToken, className)} onClick={onClick}>
      {children}
    </div>
  );
}

const stringResolver = (value: string) => value;

export type StringListInputProps = TokenListInputProps<string> & {
  values: string[];
  options?: string[];
  onChange: (arg1: string[]) => unknown;
};

export function StringListInput({
  values,
  options = [],
  onChange,
  ...props
}: StringListInputProps) {
  return (
    <TokenListInput
      {...props}
      values={values}
      options={options}
      onChange={onChange}
      resolveLabel={stringResolver}
      resolveKey={stringResolver}
      makeValue={stringResolver}
      makeArbitraryValue={stringResolver}
      isArbitrary={(value) => !options.includes(value)}
    />
  );
}

export type ValueListInputProps<V> = TokenListInputProps<V> & {
  type: () => V;
};

export function ValueListInput<V>({
  values,
  options,
  onChange,
  type,
  ...props
}: ValueListInputProps<V>) {
  const resolver = (value: V) => String(value);
  return (
    <TokenListInput
      {...props}
      values={values}
      options={options}
      onChange={onChange}
      resolveLabel={resolver}
      resolveKey={resolver}
      isArbitrary={(value) => !options.includes(value)}
      makeValue={type}
      makeArbitraryValue={type}
    />
  );
}

export type CustomTokenListInputProps = {
  values: string[];
  onChange: (arg1: string[]) => unknown;
  name?: string;
  components?: {
    Token?: React.ComponentType<TokenProps<string>>;
  };
  baseClassNames?: TokenListInputClassNames;
  classNames?: TokenListInputClassNames;
  placeholder?: string | null | undefined;
  enableListPasting?: boolean;
  disabled?: boolean;
  isExpandable?: boolean;
  inputProps?: React.ComponentProps<'input'>;
};

export function CustomTokenListInput({
  values,
  onChange,
  name,
  placeholder,
  classNames,
  components: {Token = DefaultToken, ...components} = {},
  enableListPasting,
  disabled,
  inputProps,
  ...props
}: CustomTokenListInputProps) {
  const [state, dispatch, baseInputProps, handleContainerMouseDown] =
    useTokenInput({
      onAddValue(newValue: string) {
        onChange([...values, newValue.trim()]);
      },
      onBackspace() {
        onChange(values.slice(0, -1));
      },
    });

  const tokens = useTokens({
    values,
    onChange,
    inputRef: baseInputProps.ref,
    enableListPasting,
    disabled,
    resolveLabel: stringResolver,
    resolveKey: stringResolver,
    Token,
  });

  return (
    <div className={classify(css.container, classNames?.outerContainer)}>
      <BasicInput
        // @ts-expect-error - TS2322 - Type '{ onBlur: (_event: React.SyntheticEvent<HTMLInputElement>) => void; onFocus: (_event: React.SyntheticEvent<HTMLInputElement>) => void; onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void; ... 306 more ...; onTransitionStartCapture?: React.TransitionEventHandler<...> | undefined; }' is not assignable to type '{ disabled?: unknown; } & ClassAttributes<HTMLInputElement> & InputHTMLAttributes<HTMLInputElement>'.
        inputProps={{
          ...inputProps,
          disabled,
          ...baseInputProps,
        }}
        isFocused={state.focused}
        onContainerMouseDown={handleContainerMouseDown}
        {...props}
      >
        {tokens}
        {!state.searchTerm &&
          !state.focused &&
          tokens.length === 0 &&
          placeholder && (
            <div className={css.selectorPlaceholder}>{placeholder}</div>
          )}
      </BasicInput>
      {values.map((option) => (
        <input type="hidden" name={name} key={option} value={option} />
      ))}
    </div>
  );
}

export const DefaultValueComponent = <V extends unknown>({
  value,
  resolveLabel,
}: {
  value: V;
  resolveLabel: (
    arg1: V,
  ) => string | React.ReactElement<React.ComponentProps<'div'>>;
}) => <div className={css.selectorValue}>{resolveLabel(value)}</div>;

export type SelectProps<V> = {
  value: V | null | undefined;
  onChange: (arg1?: V | null | undefined) => unknown;
  name?: string;
  clearable?: boolean;
  disabled?: boolean;
  autofocus?: boolean;
  placeholder?: string;
  hasLabel?: boolean;
  inputProps?: {
    onPaste?: (event: ClipboardEvent) => unknown;
  } & React.ComponentProps<'input'>;
  optionsDirection?: OptionsDirection;
  classNames?: InputAndOptionsClassNames;
  baseClassNames?: InputAndOptionsClassNames;
  error?: unknown;
  errorText?: string;
  showAllOptions?: unknown;
  components?: SuggestionsComponents<V>;
} & SenseTypeaheadProps<V> & {
    resolveLabel?: (
      arg1: V,
    ) => string | React.ReactElement<React.ComponentProps<'div'>>;
    resolveKey?: (arg1: V) => string;
  };

/**
 * an input that allows selecting a value from a limited set of options.
 *
 * Very similar to the HTML `select` tag but with more bells and whistles.
 *
 * @param  props.value  the controlled value
 * @param  props.onChange  the handler that is fired when the user tries to change the value
 * @param  props.name  the name of the input (useful if it is part of a form)
 * @param  props.limit  the maximum number of item the user can add
 * @param  props.TokenComponent  a custom component used to render each token within the input
 *
 * @param  props.options  total list of allowed values for this field. will be used to provide suggestions.
 * @param  props.searchable  whether or not to filter the list of options when the user types in the input
 * @param  props.allowArbitraryValues  whether or not the user can specify their own custom values
 * @param  props.validateArbitraryValue  a function that determines if a custom value is valid
 * @param  props.formatArbitraryOptionLabel  a function that customizes the custom value when it shows up as a suggestion
 * @param  props.searchOnEmptyString  whether or not to show suggestions when there is no text in the search input
 * @param  props.groupOptionsBy  a function that produces a category key for any given option. if specified, suggestions will be grouped into sections by category.
 * @param  props.sortOptionsBy  a comparison function that determines the sort order of suggestions. by default it sorts them alphabetically.
 */
export function Select<V>(props: SelectProps<V>) {
  const handleAddOption = (option: V) => {
    onChange(option);
    inputProps.ref.current?.blur();
  };
  const {
    value,
    onChange,
    name,
    searchable,
    clearable,
    disabled,
    autofocus,
    placeholder,
    hasLabel,
    optionsDirection,
    baseClassNames = legacyCss,
    classNames,
    error,
    errorText,
    showAllOptions,
    components = {},
    // @ts-expect-error - TS2339 - Property 'value' does not exist on type 'V'.
    resolveKey = (option: V) => option.value,
    // @ts-expect-error - TS2339 - Property 'label' does not exist on type 'V'.
    resolveLabel = (option: V) => option.label,
  } = props;

  const {ValueComponent = DefaultValueComponent} = components;

  const [
    {selectedIndex},
    filteredOptions,
    groupedOptions,
    inputProps,
    handleContainerMouseDown,
    handleSelect,
    {isSearching, focused, isShowingOptions},
  ] = useSenseTypeahead({
    onAddOption: handleAddOption,
    ...props,
    resolveKey,
    resolveLabel,
  });

  const showClearButton = clearable && value && !disabled;
  const showChevron = !focused && !showClearButton && !disabled;

  // returns true when a value exists or can be determined using
  // resolveKey (which is used to fetch V typed values)
  const hasValue = (data?: V | null): boolean => {
    if (data == null) {
      return false;
    }

    if (resolveKey(data) == null) {
      return false;
    }

    return true;
  };

  return (
    <InputAndOptions
      error={error}
      errorText={errorText}
      baseClassNames={baseClassNames}
      classNames={classNames}
      options={props.groupOptionsBy ? groupedOptions : filteredOptions}
      optionsDirection={optionsDirection}
      showAllOptions={showAllOptions}
      selectedIndex={searchable ? selectedIndex : null}
      onSelect={handleSelect}
      onContainerMouseDown={handleContainerMouseDown}
      components={components}
      inputProps={{
        ...props.inputProps,
        // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'outerRef' does not exist in type '{ disabled?: unknown; } & ClassAttributes<HTMLInputElement> & InputHTMLAttributes<HTMLInputElement>'.
        outerRef: props.inputProps?.ref,
        disabled,
        ...inputProps,
      }}
      isSearching={isSearching}
      isFocused={focused}
      isShowingOptions={isShowingOptions}
      resolveLabel={resolveLabel}
      resolveKey={resolveKey}
      suffixLabel={
        showClearButton ? (
          <div className={css.clearButton} onMouseDown={() => onChange(null)}>
            <CancelIcon />
          </div>
        ) : (
          showChevron && (
            <div>
              <ArrowDownIcon
                className={classify(
                  css.chevron,
                  optionsDirection && css[optionsDirection],
                )}
              />
            </div>
          )
        )
      }
      autofocus={autofocus}
      // @ts-expect-error - TS17001 - JSX elements cannot have multiple attributes with the same name.
      resolveLabel={resolveLabel}
      resolveKey={resolveKey}
    >
      {!isSearching && value != null && (
        <ValueComponent value={value} resolveLabel={resolveLabel} />
      )}
      {placeholder && (
        <Placeholder
          className={baseClassNames.placeholder}
          placeholder={placeholder}
          isLabel={hasLabel}
          isHidden={isSearching || hasValue(value)}
        />
      )}
      <input
        type="hidden"
        name={name}
        value={value != null ? resolveKey(value) : null}
      />
    </InputAndOptions>
  );
}

type CommonMenuProps<V, O> = {
  name?: string;
  value: V | null | undefined;
  baseClassNames?: InputAndOptionsClassNames;
  classNames?: InputAndOptionsClassNames;
  disabled?: boolean;
  options: O[];
  optionsDirection?: OptionsDirection;
  placeholder?: string;
};

// NOTE (kyle): this is not a great pattern. it works for this case,
// but it will break down if we have to expand the union to include other
// prop constraints.
export type MenuProps<V, O> =
  | (CommonMenuProps<V, O> & {
      clearable?: false;
      onChange: (arg1: Option) => unknown;
    })
  | (CommonMenuProps<V, O> & {
      clearable: true;
      onChange: (arg1?: Option | null | undefined) => unknown;
    });

export function Menu<
  V,
  O extends {
    value: V;
    label: string;
  },
>(
  props: MenuProps<V, O> &
    Omit<
      Omit<
        Flow_ComponentProps<typeof InputAndOptions>,
        | 'optionsDirection'
        | 'options'
        | 'onSelect'
        | 'onContainerMouseDown'
        | 'inputProps'
        | 'isFocused'
        | 'isShowingOptions'
        | 'suffixLabel'
        | 'className'
        | 'onMouseDown'
        | 'className'
      >,
      keyof MenuProps<V, O>
    >,
) {
  const handleSelect = (option: O) => {
    onChange(option);
    setTimeout(() => {
      inputProps.ref.current?.blur();
    }, 0);
  };

  const {
    value,
    onChange,
    disabled,
    options,
    optionsDirection,
    name,
    placeholder,
    clearable,
    ...subProps
  } = props;
  const valueLabel = options.find((option) => option.value === value)?.label;

  const [{focused}, inputProps, handleContainerMouseDown] = useSelect({
    options,
  });

  const showClearButton = clearable && valueLabel && !disabled;
  const showChevron = !focused && !showClearButton && !disabled;

  return (
    <InputAndOptions
      {...subProps}
      optionsDirection={optionsDirection}
      options={options}
      // @ts-expect-error - TS2322 - Type '(option: O) => void' is not assignable to type '(arg1: unknown) => unknown'.
      onSelect={handleSelect}
      onContainerMouseDown={handleContainerMouseDown}
      // @ts-expect-error - TS2322 - Type '{ onBlur: (_event: React.SyntheticEvent<HTMLInputElement>) => void; onFocus: (_event: React.SyntheticEvent<HTMLInputElement>) => void; onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void; ... 4 more ...; placeholder: string | undefined; }' is not assignable to type '{ disabled?: unknown; } & ClassAttributes<HTMLInputElement> & InputHTMLAttributes<HTMLInputElement>'.
      inputProps={{
        disabled,
        name,
        placeholder,
        ...inputProps,
      }}
      isFocused={focused}
      isShowingOptions={focused}
      suffixLabel={
        props.clearable && showClearButton ? (
          <div
            className={css.clearButton}
            onMouseDown={() => props.onChange(null)}
          >
            <CancelIcon />
          </div>
        ) : (
          showChevron && (
            <div>
              <ArrowDownIcon
                className={classify(
                  css.chevron,
                  optionsDirection && css[optionsDirection],
                )}
              />
            </div>
          )
        )
      }
    >
      {valueLabel ? (
        <div className={css.selectorValue}>{String(valueLabel)}</div>
      ) : null}
    </InputAndOptions>
  );
}

type SuggestionsClassNames = Readonly<{
  options?: string | null | undefined;
  down?: string | null | undefined;
  up?: string | null | undefined;
  showAll?: string | null | undefined;
  option?: string | null | undefined;
  selected?: string | null | undefined;
  optionGroup?: string | null | undefined;
  optionGroupHeading?: string | null | undefined;
}>;

type SuggestionsComponents<V> = {
  Suggestion?: React.ComponentType<SuggestionProps<V>>;
  ValueComponent?: React.ComponentType<{
    value: V;
    resolveLabel: (
      arg1: V,
    ) => string | React.ReactElement<React.ComponentProps<'div'>>;
  }>;
};

type OptionsDirection = 'up' | 'down';

export type SuggestionProps<V> = {
  className: string;
  onClick: (arg1: React.SyntheticEvent) => unknown;
  option: V;
  isArbitrary: boolean;
  isSelected: boolean;
  resolveLabel: (
    arg1: V,
  ) => string | React.ReactElement<React.ComponentProps<'div'>>;
};

type CommonSuggestionsProps<V> = {
  selectedIndex: number | null | undefined;
  baseClassNames: SuggestionsClassNames;
  classNames?: SuggestionsClassNames;
  onOptionClick: (arg1: V, arg2: React.SyntheticEvent) => unknown;
  // TODO (kyle): not sure if we need this
  suggestionProps?: SuggestionProps<V>;
  components?: SuggestionsComponents<V>;
  resolveKey: (arg1: V) => string;
  resolveLabel: (
    arg1: V,
  ) => string | React.ReactElement<React.ComponentProps<'div'>>;
  isArbitrary: (arg1: V) => boolean;
};

function SuggestionsGroup<V>({
  baseClassNames,
  classNames,
  options,
  selectedIndex,
  onOptionClick,
  suggestionProps,
  components: {Suggestion = DefaultSuggestion} = {},
  resolveKey,
  resolveLabel,
  isArbitrary,
}: CommonSuggestionsProps<V> & {
  options: V[];
}) {
  return options.map((option, index) => {
    const isSelected = index === selectedIndex;
    return (
      <Suggestion
        className={classify(
          baseClassNames.option,
          classNames?.option,
          isSelected && baseClassNames.selected,
        )}
        key={resolveKey(option)}
        onClick={(event) => onOptionClick(option, event)}
        option={option}
        isSelected={isSelected}
        resolveLabel={resolveLabel}
        isArbitrary={isArbitrary(option)}
        {...suggestionProps}
      />
    );
  });
}

export function Suggestions<V>({
  options,
  baseClassNames,
  classNames,
  direction,
  showAll,
  ...props
}: CommonSuggestionsProps<V> & {
  options: V[] | Map<string, V[]>;
  direction: OptionsDirection;
  showAll?: unknown;
}): React.ReactElement<React.ComponentProps<'div'>> {
  return (
    <div
      className={classify(
        baseClassNames.options,
        baseClassNames[direction],
        classNames?.options,
        showAll && baseClassNames.showAll,
      )}
    >
      {options instanceof Map ? (
        <GroupedSuggestions
          {...props}
          options={options}
          baseClassNames={baseClassNames}
          classNames={classNames}
        />
      ) : (
        <SuggestionsGroup
          {...props}
          options={options}
          baseClassNames={baseClassNames}
          classNames={classNames}
        />
      )}
    </div>
  );
}

function GroupedSuggestions<V>({
  baseClassNames,
  options,
  selectedIndex,
  ...props
}: CommonSuggestionsProps<V> & {
  options: Map<string, V[]>;
}) {
  const {t, makeKey} = useLabelTranslation();
  let runningTotal = 0;
  return Array.from(options).map(([groupName, group]: [any, any]) => {
    const section = (
      // @ts-expect-error - TS2322 - Type 'string | null | undefined' is not assignable to type 'string | undefined'.
      <section className={baseClassNames.optionGroup} key={groupName}>
        {/* @ts-expect-error - TS2322 - Type 'string | null | undefined' is not assignable to type 'string | undefined'. */}
        <h4 className={baseClassNames.optionGroupHeading}>
          {t(makeKey(groupName), groupName)}
        </h4>
        <SuggestionsGroup
          {...props}
          baseClassNames={baseClassNames}
          selectedIndex={
            selectedIndex != null ? selectedIndex - runningTotal : null
          }
          options={group}
        />
      </section>
    );
    runningTotal += group.length;
    return section;
  });
}

export const DefaultSuggestion = <V extends unknown>({
  option,
  isSelected: _,
  resolveLabel,
  isArbitrary,
  ...props
}: SuggestionProps<V>) => {
  const label = resolveLabel(option);
  const {t, makeKey} = useLabelTranslation();

  return (
    <div
      {...props}
      data-qa-id={`suggestion-${kebabCase(
        typeof label === 'string' ? label : String(option),
      )}`}
    >
      {isArbitrary && typeof label === 'string'
        ? `${t('Create', 'Create')} "${label}"`
        : label}
    </div>
  );
};

export type InputAndOptionsClassNames = Readonly<
  SuggestionsClassNames & {
    container?: string | null | undefined;
    placeholder?: string | null | undefined;
    box?: string | null | undefined;
    up?: string | null | undefined;
    down?: string | null | undefined;
    inputDisabled?: string | null | undefined;
    isSearching?: string | null | undefined;
    focused?: string | null | undefined;
    withOptions?: string | null | undefined;
    withPrefix?: string | null | undefined;
    withSuffix?: string | null | undefined;
    withError?: string | null | undefined;
    withVerticalStretch?: string | null | undefined;
    withHeightLimit?: string | null | undefined;
  }
>;

type RequiredInputAndOptionsProps<V> = {
  options: V[] | Map<string, V[]>;
  onSelect: (arg1: V) => unknown;
  onContainerMouseDown: (arg1: React.MouseEvent<HTMLInputElement>) => unknown;
  inputProps: {
    disabled?: unknown;
  } & React.ComponentProps<'input'>;
};

type OptionalInputAndOptionsProps<V> = {
  children?: React.ReactNode;
  baseClassNames?: InputAndOptionsClassNames;
  classNames?: InputAndOptionsClassNames;
  components?: SuggestionsComponents<V>;
  selectedIndex?: number | null | undefined;
  onPaste?: (event: ClipboardEvent) => unknown;
  isSearching?: unknown;
  isFocused?: unknown;
  isShowingOptions?: unknown;
  isLimited?: unknown;
  isExpandable?: unknown;
  isHeightLimited?: unknown;
  prefixLabel?: React.ReactNode;
  suffixLabel?: React.ReactNode;
  optionsDirection?: OptionsDirection;
  error?: unknown;
  showAllOptions?: unknown;
  autofocus?: unknown;
  resolveKey?: (arg1: V) => string;
  resolveLabel?: (
    arg1: V,
  ) => string | React.ReactElement<React.ComponentProps<'div'>>;
  isArbitrary?: (arg1: V) => boolean;
  errorText?: string;
};

type InputAndOptionsProps<V> = RequiredInputAndOptionsProps<V> &
  OptionalInputAndOptionsProps<V>;

export function InputAndOptions<V = Option>({
  baseClassNames = legacyCss,
  classNames = {},
  components,
  options,
  selectedIndex,
  onSelect,
  optionsDirection = 'down',
  showAllOptions,
  autofocus,
  // @ts-expect-error - TS2339 - Property 'value' does not exist on type 'V'.
  resolveKey = (option: V) => option.value,
  // @ts-expect-error - TS2339 - Property 'label' does not exist on type 'V'.
  resolveLabel = (option: V) => option.label,
  // @ts-expect-error - TS2339 - Property 'arbitrary' does not exist on type 'V'.
  isArbitrary = (option: V) => option.arbitrary,
  ...props
}: InputAndOptionsProps<V>) {
  if (Array.isArray(options)) {
    if (uniq(options.map(resolveKey)).length !== options.length) {
      logger.warn(
        `resolveKey must resolve a unique id for each item or search will throw`,
      );
    }
  }
  return (
    <BasicInput
      baseClassNames={baseClassNames}
      classNames={classNames}
      optionsDirection={optionsDirection}
      {...props}
      lowerContent={
        Boolean(props.isShowingOptions) && (
          <Suggestions
            baseClassNames={baseClassNames}
            classNames={classNames}
            components={components}
            direction={optionsDirection}
            options={options}
            selectedIndex={selectedIndex}
            onOptionClick={onSelect}
            showAll={showAllOptions}
            resolveKey={resolveKey}
            resolveLabel={resolveLabel}
            isArbitrary={isArbitrary}
          />
        )
      }
    />
  );
}

// TODO (kyle): put this inside InputAndOptions
function BasicInput({
  children,
  baseClassNames = legacyCss,
  classNames = {},
  onContainerMouseDown,
  inputProps,
  isSearching,
  isFocused,
  isLimited,
  isShowingOptions,
  isExpandable,
  isHeightLimited,
  prefixLabel,
  suffixLabel,
  optionsDirection = 'down',
  error,
  errorText,
  lowerContent,
  autofocus,
  autoComplete = 'no',
}: {
  onContainerMouseDown: (arg1: React.MouseEvent<HTMLInputElement>) => unknown;
  inputProps: {
    disabled?: unknown;
  } & React.ComponentProps<'input'>;
  children?: React.ReactNode;
  baseClassNames?: InputAndOptionsClassNames;
  classNames?: InputAndOptionsClassNames;
  selectedIndex?: number | null | undefined;
  onPaste?: (event: ClipboardEvent) => unknown;
  isSearching?: unknown;
  isFocused?: unknown;
  isShowingOptions?: unknown;
  isLimited?: unknown;
  isExpandable?: unknown;
  isHeightLimited?: unknown;
  prefixLabel?: React.ReactNode;
  suffixLabel?: React.ReactNode;
  optionsDirection?: OptionsDirection;
  error?: unknown;
  errorText?: string;
  lowerContent?: React.ReactNode;
  autofocus?: unknown;
  // 'no' is nonstandard but we probably want it because chrome needs it in order to disable
  // autocomplete, https://bugs.chromium.org/p/chromium/issues/detail?id=914451
  autoComplete?: 'on' | 'off' | 'no';
}) {
  if (!inputProps.type) {
    inputProps.type = 'text';
  }

  React.useLayoutEffect(() => {
    if (autofocus) {
      // @ts-expect-error - TS18049 - 'inputProps.ref' is possibly 'null' or 'undefined'. | TS2339 - Property 'current' does not exist on type '((instance: HTMLInputElement | null) => void | (() => VoidOrUndefinedOnly)) | RefObject<HTMLInputElement | null>'.
      inputProps.ref.current?.focus();
    }
  }, []);

  // @ts-expect-error - TS2339 - Property 'outerRef' does not exist on type '{ disabled?: unknown; } & ClassAttributes<HTMLInputElement> & InputHTMLAttributes<HTMLInputElement>'.
  const {outerRef, ...inputPropsRest} = inputProps;

  return (
    <div
      className={classify(baseClassNames.container, classNames.container)}
      onMouseDown={onContainerMouseDown}
    >
      <div
        className={classify(
          baseClassNames.box,
          baseClassNames[optionsDirection],
          classNames.box,
          classNames[optionsDirection],
          inputProps.disabled && baseClassNames.inputDisabled,
          inputProps.disabled && classNames.inputDisabled,
          isSearching && baseClassNames.isSearching,
          isSearching && classNames.isSearching,
          isFocused && baseClassNames.focused,
          isFocused && classNames.focused,
          isShowingOptions && baseClassNames.withOptions,
          isShowingOptions && classNames.withOptions,
          prefixLabel && baseClassNames.withPrefix,
          prefixLabel && classNames.withPrefix,
          suffixLabel && baseClassNames.withSuffix,
          suffixLabel && classNames.withSuffix,
          error && baseClassNames.withError,
          error && classNames.withError,
          isExpandable && baseClassNames.withVerticalStretch,
          isExpandable && classNames.withVerticalStretch,
          isHeightLimited && baseClassNames.withHeightLimit,
          isHeightLimited && classNames.withHeightLimit,
        )}
      >
        {prefixLabel}
        {children}
        <input
          {...inputPropsRest}
          autoComplete={autoComplete}
          ref={(el) => {
            // @ts-expect-error - TS18049 - 'inputProps.ref' is possibly 'null' or 'undefined'. | TS2339 - Property 'current' does not exist on type '((instance: HTMLInputElement | null) => void | (() => VoidOrUndefinedOnly)) | RefObject<HTMLInputElement | null>'.
            inputProps.ref.current = el;
            if (outerRef) {
              outerRef.current = el;
            }
          }}
          className={css.selectorSearch}
          style={{
            // @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'Visibility | undefined'.
            visibility: isLimited && 'hidden',
          }}
        />
        {suffixLabel}
      </div>
      {lowerContent}
      {errorText && <span className={css.error}>{errorText}</span>}
    </div>
  );
}

function useTokens<V>({
  values,
  onChange,
  resolveKey,
  resolveLabel,
  inputRef,
  enableListPasting,
  Token,
  disabled,
}: {
  values: V[];
  onChange: (arg1: V[]) => unknown;
  resolveKey: (arg1: V) => string;
  resolveLabel: (
    arg1: V,
  ) => string | React.ReactElement<React.ComponentProps<'div'>>;
  inputRef: {
    current: HTMLInputElement | null | undefined;
  };
  enableListPasting?: boolean;
  Token: React.ComponentType<TokenProps<V>>;
  disabled?: boolean;
  // @ts-expect-error TS2304
}): Array<__RemoveMe__> {
  const handleClickOption = React.useMemo(
    () => (optionToRemove: V) => {
      onChange(
        values.filter(
          (option) => resolveKey(option) !== resolveKey(optionToRemove),
        ),
      );
      inputRef.current?.focus();
    },
    [values, onChange],
  );

  let tokens:
    | Array<
        React.ReactElement<
          React.ComponentProps<React.ComponentType<TokenProps<V>>>
        >
      >
    | Array<Node>
    | Array<never> = [];

  if (enableListPasting && values.length > 100) {
    // @ts-expect-error - TS2345 - Argument of type 'V | undefined' is not assignable to parameter of type 'V'.
    const truncatedTokenContents = `${resolveLabel(first(values))}
+ ${values.length - 2} ${values.length === 1 ? 'other' : 'others'}
${resolveLabel(last(values) as V)}`;

    tokens = [
      <Token
        className={css.selectorTruncatedValue}
        key="truncatedToken"
        onClick={() => onChange([])}
      >
        {truncatedTokenContents}
      </Token>,
    ];
  } else {
    tokens = values.map((option) => (
      <Token
        className={css.selectorValue}
        key={resolveKey(option)}
        option={option}
        onClick={!disabled ? () => handleClickOption(option) : null}
      >
        {resolveLabel(option)}
      </Token>
    ));
  }

  return tokens;
}

export function Placeholder({
  className,
  placeholder,
  isLabel,
  isHidden,
}: {
  className?: string | null | undefined;
  placeholder?: string | null | undefined;
  isLabel?: boolean;
  isHidden?: boolean;
}) {
  return (
    <div
      className={classify(
        css.selectorPlaceholder,
        className,
        isLabel && css.isLabel,
        isHidden && css.hidden,
      )}
    >
      {placeholder}
    </div>
  );
}
